import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Building2, ArrowRight, MessageSquare, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Brand";
import { ThemeToggle } from "../components/ThemeToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import Messages from "./admin/Messages";

export default function ProcessorDashboard() {
  const { user, logout, setActingCompany } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [chat, setChat] = useState(null); // {id, name}

  const load = useCallback(() => api.get("/messages/threads").then(({ data }) => setThreads(data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const open = (t) => { setActingCompany({ id: t.company_id, name: t.company_name }); navigate("/admin"); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-900 sticky top-0 bg-slate-950/90 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="sm" />
            <span className="hidden sm:inline text-xs font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-3 py-1">Payroll Processor</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <span className="text-sm text-slate-400 hidden sm:inline">{user?.email}</span>
            <button data-testid="processor-logout-button" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-full px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold">Assigned companies</h1>
        <p className="text-slate-400 text-sm mt-1">{threads.length} client{threads.length !== 1 ? "s" : ""} · open a company to manage payroll or start a chat</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
          {threads.map((t, i) => (
            <motion.div key={t.company_id} data-testid={`processor-company-${t.company_id}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: t.primary_color }}>{t.company_name[0]}</div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{t.company_name}</h3>
                  {t.payroll_ready && <span data-testid={`payroll-ready-${t.company_id}`} className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">● Payroll ready to pull</span>}
                  {t.last_message && <p className="text-xs text-slate-500 truncate mt-0.5">{t.last_message}</p>}
                </div>
                {t.unread > 0 && <span className="min-w-[22px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">{t.unread}</span>}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button data-testid={`processor-open-${t.company_id}`} onClick={() => open(t)} className="rounded-xl bg-blue-600 hover:bg-blue-500 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                  <Building2 className="w-4 h-4" /> Manage <ArrowRight className="w-4 h-4" />
                </button>
                <button data-testid={`processor-chat-${t.company_id}`} onClick={() => setChat({ id: t.company_id, name: t.company_name })} className="rounded-xl bg-slate-800 hover:bg-slate-700 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                  <MessageSquare className="w-4 h-4" /> Chat
                </button>
              </div>
            </motion.div>
          ))}
          {threads.length === 0 && <div className="col-span-full text-center py-16 text-slate-500">No companies assigned to you yet.</div>}
        </div>
      </main>

      <Dialog open={!!chat} onOpenChange={(o) => !o && setChat(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center justify-between">Chat · {chat?.name}<button onClick={() => setChat(null)} className="text-slate-400 hover:text-slate-200"><X className="w-4 h-4" /></button></DialogTitle></DialogHeader>
          {chat && <Messages companyId={chat.id} title={`Conversation with ${chat.name}`} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
